import { useState, useEffect } from "react";
import Select from "react-select";
import DataFetcherTemplate from "../../hooks/fetchTemplate";
import { motion } from "framer-motion";
import { ArrowRight, Plus, Filter, RotateCcw } from "lucide-react";

const facultadSlugMap = {
  empresas: "Ciencias Económicas y Empresariales",
  exactas: "Ciencias Exactas y Naturales",
  salud: "Ciencias de la Salud",
  sociales: "Ciencias Sociales, Educación y Humanidades",
  juridicas: "Ciencias Jurídicas y Políticas",
  ingenieria: "Ingenierías y Arquitectura",
  negocios: "UTPL Escuela de Negocios",
};

const facultadNameToSlug = Object.fromEntries(
  Object.entries(facultadSlugMap).map(([slug, name]) => [name, slug]),
);

const normalize = (str) =>
  str
    ?.toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const getFieldValues = (item, fields) => {
  const values = [];
  fields.forEach((field) => {
    const data = item[field];
    if (!data) return;
    if (Array.isArray(data)) values.push(...data);
    else values.push(data);
  });

  return values
    .map((v) => v?.toString().trim())
    .filter((v) => v && v.length > 0);
};

const getSplitValues = (item, fields) => {
  return getFieldValues(item, fields)
    .flatMap((v) => v.split(","))
    .map((v) => v.trim())
    .filter(Boolean);
};

function OfferPosgrados() {
  const optionsPrograma = [
    { value: "todos", label: "Todos los programas" },
    { value: "Maestría", label: "Maestría" },
    { value: "Especialización", label: "Especialización" },
    { value: "Doctorado", label: "Doctorado" },
  ];

  const optionsFacultad = [
    { value: "todos", label: "Toda la oferta" },
    {
      value: "Ciencias Económicas y Empresariales",
      label: "Ciencias Económicas y Empresariales",
    },
    {
      value: "Ciencias Exactas y Naturales",
      label: "Ciencias Exactas y Naturales",
    },
    { value: "Ciencias de la Salud", label: "Ciencias de la Salud" },
    {
      value: "Ciencias Sociales, Educación y Humanidades",
      label: "Ciencias Sociales, Educación y Humanidades",
    },
    {
      value: "Ciencias Jurídicas y Políticas",
      label: "Ciencias Jurídicas y Políticas",
    },
    {
      value: "Ingenierías y Arquitectura",
      label: "Ingenierías y Arquitectura",
    },
    { value: "UTPL Escuela de Negocios", label: "UTPL Escuela de Negocios" },
  ];

  const optionsModalidad = [
    { value: "todos", label: "Todas las modalidades" },
    { value: "En línea", label: "En línea" },
    { value: "Presencial", label: "Presencial" },
    { value: "Híbrida", label: "Híbrida" },
  ];

  const getInitialValue = (param, options) => {
    const params = new URLSearchParams(window.location.search);
    let value = params.get(param);

    if (param === "facultad" && facultadSlugMap[value]) {
      value = facultadSlugMap[value];
    }

    return options.find((o) => o.value === value) || options[0];
  };

  const [programaSeleccionado, setProgramaSeleccionado] = useState(
    getInitialValue("programa", optionsPrograma),
  );

  const [facultadSeleccionada, setFacultadSeleccionada] = useState(
    getInitialValue("facultad", optionsFacultad),
  );

  const [modalidadSeleccionada, setModalidadSeleccionada] = useState(
    getInitialValue("modalidad", optionsModalidad),
  );

  useEffect(() => {
    const params = new URLSearchParams();

    if (programaSeleccionado.value !== "todos")
      params.set("programa", programaSeleccionado.value);

    if (facultadSeleccionada.value !== "todos") {
      const slug = facultadNameToSlug[facultadSeleccionada.value];
      params.set("facultad", slug);
    }

    if (modalidadSeleccionada.value !== "todos")
      params.set("modalidad", modalidadSeleccionada.value);

    const newUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "");

    window.history.replaceState({}, "", newUrl);
  }, [programaSeleccionado, facultadSeleccionada, modalidadSeleccionada]);

  const limpiarFiltros = () => {
    setProgramaSeleccionado(optionsPrograma[0]);
    setFacultadSeleccionada(optionsFacultad[0]);
    setModalidadSeleccionada(optionsModalidad[0]);
  };

  const DataDisplayOferta = ({ data }) => {
    const filteredData = data.filter((item) => {
      const matchPrograma =
        programaSeleccionado.value === "todos" ||
        normalize(item.field_programa).includes(
          normalize(programaSeleccionado.value),
        );

      const facultades = getFieldValues(item, ["field_area"]);
      const matchFacultad =
        facultadSeleccionada.value === "todos" ||
        facultades.some((f) =>
          normalize(f).includes(normalize(facultadSeleccionada.value)),
        );

      const modalidades = getFieldValues(item, [
        "field_modalidad",
        "field_modalidad_carrera",
      ]);

      const matchModalidad =
        modalidadSeleccionada.value === "todos" ||
        modalidades.some((m) =>
          normalize(m).includes(normalize(modalidadSeleccionada.value)),
        );

      return matchPrograma && matchFacultad && matchModalidad;
    });

    return (
      <div className="px-16 md:px-10">
        {/* Filtros */}
        <div className="wrapper-filtros layout9">
          <div>
            <p>
              <b>Programa</b>
            </p>
            <Select
              options={optionsPrograma}
              value={programaSeleccionado}
              onChange={setProgramaSeleccionado}
            />
          </div>

          <div>
            <p>
              <b>Facultad</b>
            </p>
            <Select
              options={optionsFacultad}
              value={facultadSeleccionada}
              onChange={setFacultadSeleccionada}
            />
          </div>

          <div>
            <p>
              <b>Modalidad</b>
            </p>
            <Select
              options={optionsModalidad}
              value={modalidadSeleccionada}
              onChange={setModalidadSeleccionada}
            />
          </div>

          <div>
            <button
              className="btn-reset-filtros flex items-center gap-2"
              onClick={limpiarFiltros}
            >
              <RotateCcw size={16} />
              Limpiar filtros
            </button>
          </div>
        </div>

        {/* Contador */}
        <div className="mb-6 flex items-center justify-between">
          <p className="text-gray-700 flex items-center gap-2">
            <Filter size={16} />
            <span className="font-bold">{filteredData.length}</span> programas
            encontrados
          </p>
        </div>

        {/* Grid estilo OfferPresencial */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {filteredData.map((item, index) => {
            const modalidades = getFieldValues(item, [
              "field_modalidad",
              "field_modalidad_carrera",
            ]);

            const duraciones = getFieldValues(item, [
              "field_duracion",
              "field_posgrado_duracion",
            ]);

            const facultades = getFieldValues(item, ["field_area"]);

            return (
              <motion.a
                key={index}
                href={item.field_enlace_contenido}
                rel="noreferrer"
                className="group relative block bg-white rounded-2xl shadow-md overflow-hidden"
                initial="rest"
                animate="rest"
                whileHover="hover"
              >
                {/* Imagen */}
                <div className="h-40 overflow-hidden">
                  <img
                    src={item.field_portada}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                </div>

                {/* Botón + */}
                <motion.div
                  variants={{
                    rest: { scale: 1, opacity: 0.8 },
                    hover: { scale: 1.2, opacity: 1 },
                  }}
                  className="absolute top-4 right-4 bg-white/90 backdrop-blur-md rounded-full p-2 shadow-md"
                >
                  <Plus size={12} />
                </motion.div>

                {/* Contenido */}
                <div className="p-5 space-y-3">
                  <div className="flex gap-2 flex-wrap">
                    <span className="text-xs bg-blue-100 px-2 py-1 rounded">
                      {item.field_programa}
                    </span>

                    {item.field_tag?.[0] && (
                      <span className="text-xs bg-amber-400 text-white px-2 py-1 rounded">
                        {item.field_tag}
                      </span>
                    )}
                  </div>

                  <h3 className="text-base font-bold leading-tight p-0 ">
                    {item.title}
                  </h3>

                  {modalidades.length > 0 && (
                    <p className="text-sm text-gray-600 p-0 m-0 leading-none ">
                      <strong>Modalidad:</strong> {modalidades.join(", ")}
                    </p>
                  )}

                  {duraciones.length > 0 && (
                    <p className="text-sm text-gray-600 p-0 leading-none">
                      <strong>Duración:</strong> {duraciones.join(", ")}
                    </p>
                  )}

                  {facultades.length > 0 && (
                    <p className="text-sm text-gray-600 p-0 leading-none">
                      <strong>Facultad:</strong> {facultades.join(", ")}
                    </p>
                  )}

                {item.field_inicio?.[0] && (
                    <p className="text-sm text-gray-600 p-0 leading-none">
                      <b>Inicio de clases:</b> {item.field_inicio}
                    </p>
                  )}
                </div>

                {/* Overlay */}
                <motion.div
                  variants={{
                    rest: { opacity: 0 },
                    hover: { opacity: 1 },
                  }}
                  className="absolute inset-0 bg-black/10 flex items-center justify-center"
                >
                  <ArrowRight size={44} className="text-white" />
                </motion.div>
              </motion.a>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <DataFetcherTemplate url="https://utpl.edu.ec/maestrias/services/posgrados.json">
      {(data) => <DataDisplayOferta data={data} />}
    </DataFetcherTemplate>
  );
}

export default OfferPosgrados;
